import React, { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import PoseAlignFeedback from './checkin/PoseAlignFeedback';
import { detectKeypoints } from '../lib/poseDetector';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { ZoomIn, ZoomOut, Check, X, Layers, User, EyeOff } from 'lucide-react';
import { uploadToCloudinary } from '../utils/cloudinaryUpload';
import BodySilhouetteOverlay, { poseToVariant, poseToPhotoKey } from './BodySilhouetteOverlay';

function getTouchDistance(t1, t2) {
  const dx = t1.clientX - t2.clientX;
  const dy = t1.clientY - t2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

/* ── Elegant pose guide overlay for check-in photos ── */
function PoseGuide({ pose }) {
  if (!pose) return null;
  const b = 'absolute w-6 h-6 pointer-events-none';
  return (
    <>
      {/* Corner brackets — camera viewfinder style */}
      <div className={`${b} top-[4%] left-[4%] border-t-2 border-l-2 border-white/55`} />
      <div className={`${b} top-[4%] right-[4%] border-t-2 border-r-2 border-white/55`} />
      <div className={`${b} bottom-[4%] left-[4%] border-b-2 border-l-2 border-white/55`} />
      <div className={`${b} bottom-[4%] right-[4%] border-b-2 border-r-2 border-white/55`} />
      {/* Center alignment guide */}
      <div className="absolute left-1/2 top-0 bottom-0 w-px border-l border-dashed border-white/20 pointer-events-none -translate-x-1/2" />
      {/* Pose name label */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/55 text-white text-xs font-semibold tracking-wide px-3 py-1 rounded-full pointer-events-none whitespace-nowrap select-none">
        {pose}
      </div>
    </>
  );
}

export default function ImageCropper({ open, onClose, imageSrc, onCropComplete, aspectRatio = 1, title = null, quality = 0.82, pose = null, circular = false, uploadContext = null, previousPhoto = null }) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [img, setImg] = useState(null);
  // Initialize to the requested aspect ratio (NOT a hardcoded 3:4). The crop
  // container is sized by `containerSize`, and the ghost/silhouette overlays
  // fill it with object-cover. If this starts at the wrong aspect, a 9:16
  // check-in ghost gets cropped top+bottom (head cut) until the layout effect
  // corrects it — and that effect can bail (ref not yet attached) and never
  // re-run, leaving the cut persistent. Deriving from aspectRatio makes the
  // box the right shape from the very first render.
  const [containerSize, setContainerSize] = useState(() => {
    const w = 300;
    return { w, h: Math.round(w / aspectRatio) };
  });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const pinchRef = useRef({ active: false, startDist: 0, startZoom: 1 });

  // Overlay mode: ghost (previous photo), silhouette (body outline), or off
  const [overlayMode, setOverlayMode] = useState('off');

  // Pose detection state
  const [detection, setDetection] = useState(null); // { quality, reason, keypoints, poseType } | null
  const detectionSeqRef = useRef(0);
  const poseKey = pose ? poseToPhotoKey(pose).replace('photo_', '') : null;
  const canPoseCheck = !!pose && uploadContext === 'checkin';

  useEffect(() => {
    if (!open) return;
    if (previousPhoto) setOverlayMode('ghost');
    else if (pose) setOverlayMode('silhouette');
    else setOverlayMode('off');
  }, [open, previousPhoto, pose]);

  // Debounced pose detection on every zoom/pan change
  useEffect(() => {
    if (!canPoseCheck || !img || !open) return;
    const seq = ++detectionSeqRef.current;
    const timer = setTimeout(async () => {
      // Larger detection canvas = better landmark scores on mid-range mobile.
      // MediaPipe Pose Lite resizes internally to 256px; feeding it pre-scaled
      // input below that costs accuracy — especially visibility on features
      // like bare shoulders where contrast is lower.
      const DETECT_W = 512;
      const canvas = document.createElement('canvas');
      canvas.width = DETECT_W;
      canvas.height = Math.round(DETECT_W / aspectRatio);
      const ctx = canvas.getContext('2d');
      const { w, h } = containerSize;
      const scale = Math.max(canvas.width / img.width, canvas.height / img.height) * zoom;
      const dw = img.width * scale, dh = img.height * scale;
      const dx = (canvas.width - dw) / 2 + offset.x * (canvas.width / w);
      const dy = (canvas.height - dh) / 2 + offset.y * (canvas.height / h);
      ctx.drawImage(img, dx, dy, dw, dh);
      const result = await detectKeypoints(canvas, poseKey);
      if (seq === detectionSeqRef.current) setDetection(result);
    }, 350);
    return () => clearTimeout(timer);
  }, [canPoseCheck, img, open, zoom, offset.x, offset.y, containerSize.w, containerSize.h, aspectRatio, poseKey]);

  const cycleOverlayMode = useCallback(() => {
    setOverlayMode((prev) => {
      if (previousPhoto) {
        // ghost → silhouette → off → ghost
        if (prev === 'ghost') return 'silhouette';
        if (prev === 'silhouette') return 'off';
        return 'ghost';
      }
      // silhouette → off → silhouette
      return prev === 'silhouette' ? 'off' : 'silhouette';
    });
  }, [previousPhoto]);

  useEffect(() => {
    if (!imageSrc) return;
    const image = new Image();
    // Enable CORS for cross-origin URLs (e.g. Cloudinary). Required when
    // re-cropping an already-uploaded photo — without this the canvas becomes
    // tainted, toDataURL() throws SecurityError, and MediaPipe pixel reads
    // fail with "Pose detection unavailable". data: URLs ignore this flag.
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      setImg(image);
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    };
    image.src = imageSrc;
  }, [imageSrc]);

  useLayoutEffect(() => {
    if (!open || !containerRef.current) return;
    const el = containerRef.current;
    const apply = (availW) => {
      if (!availW) return;
      const maxH = Math.max(200, window.innerHeight - 260);
      const idealH = Math.round(availW / aspectRatio);
      if (idealH <= maxH) {
        // Width is the constraint — height fits naturally
        setContainerSize({ w: availW, h: idealH });
      } else {
        // Height is the constraint — shrink width to maintain exact aspect ratio
        const w = Math.round(maxH * aspectRatio);
        setContainerSize({ w, h: maxH });
      }
    };
    // Immediate measurement
    apply(el.clientWidth);
    // ResizeObserver uses entry.contentRect for accurate post-animation size
    const ro = new ResizeObserver((entries) => {
      apply(Math.round(entries[0].contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, aspectRatio]);

  const drawCanvas = useCallback(() => {
    if (!canvasRef.current || !img) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { w, h } = containerSize;
    canvas.width = w;
    canvas.height = h;
    ctx.clearRect(0, 0, w, h);

    const scale = Math.max(w / img.width, h / img.height) * zoom;
    const drawW = img.width * scale;
    const drawH = img.height * scale;
    const x = (w - drawW) / 2 + offset.x;
    const y = (h - drawH) / 2 + offset.y;

    ctx.drawImage(img, x, y, drawW, drawH);
  }, [img, zoom, offset, containerSize]);

  useEffect(() => { drawCanvas(); }, [drawCanvas]);

  // Clamp pan offset so the image always covers the canvas. Without this, panning
  // past the edge exposes transparent canvas, which JPEG export bakes as BLACK
  // (no alpha channel) — visible as black bars in the final uploaded photo.
  const clampOffset = useCallback((off, zoomVal = zoom) => {
    if (!img) return off;
    const { w, h } = containerSize;
    const scale = Math.max(w / img.width, h / img.height) * zoomVal;
    const drawW = img.width * scale;
    const drawH = img.height * scale;
    const maxX = Math.max(0, (drawW - w) / 2);
    const maxY = Math.max(0, (drawH - h) / 2);
    return {
      x: Math.max(-maxX, Math.min(maxX, off.x)),
      y: Math.max(-maxY, Math.min(maxY, off.y)),
    };
  }, [img, containerSize, zoom]);

  // Re-clamp offset when zoom changes — zooming out shrinks the valid pan range.
  useEffect(() => {
    setOffset(prev => clampOffset(prev, zoom));
  }, [zoom, clampOffset]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    e.preventDefault();
    setOffset(clampOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y }));
  };

  const handleMouseUp = () => setDragging(false);

  const handleTouchStart = (e) => {
    e.preventDefault();
    if (e.touches.length === 2) {
      pinchRef.current = {
        active: true,
        startDist: getTouchDistance(e.touches[0], e.touches[1]),
        startZoom: zoom,
      };
      setDragging(false);
    } else if (e.touches.length === 1) {
      pinchRef.current.active = false;
      setDragging(true);
      setDragStart({ x: e.touches[0].clientX - offset.x, y: e.touches[0].clientY - offset.y });
    }
  };

  const handleTouchMove = (e) => {
    e.preventDefault();
    if (e.touches.length === 2 && pinchRef.current.active) {
      const currentDist = getTouchDistance(e.touches[0], e.touches[1]);
      const scale = currentDist / pinchRef.current.startDist;
      setZoom(Math.min(3, Math.max(1, pinchRef.current.startZoom * scale)));
    } else if (e.touches.length === 1 && dragging && !pinchRef.current.active) {
      setOffset(clampOffset({ x: e.touches[0].clientX - dragStart.x, y: e.touches[0].clientY - dragStart.y }));
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) pinchRef.current.active = false;
    if (e.touches.length === 0) setDragging(false);
  };

  const handleCrop = async () => {
    if (!img) return;
    const QUALITY = quality;

    // Output at 720×1280 for portrait (9:16) photos.
    // Optimized for mobile display while keeping upload payload small.
    const outW = aspectRatio < 1 ? 720 : 1280;
    const outH = aspectRatio < 1 ? 1280 : Math.round(outW / aspectRatio);

    const out = document.createElement('canvas');
    out.width = outW;
    out.height = outH;
    const ctx = out.getContext('2d');

    // Replicate the same transform as the preview, but scaled to output size
    const { w: prevW, h: prevH } = containerSize;
    const scaleX = outW / prevW;
    const scaleY = outH / prevH;

    // Compute image draw params (same logic as drawCanvas but for output size)
    const fitScale = Math.max(outW / img.width, outH / img.height) * zoom;
    const drawW = img.width * fitScale;
    const drawH = img.height * fitScale;
    const x = (outW - drawW) / 2 + offset.x * scaleX;
    const y = (outH - drawH) / 2 + offset.y * scaleY;

    ctx.drawImage(img, x, y, drawW, drawH);

    const dataUrl = out.toDataURL('image/jpeg', QUALITY);

    const finalDetection = canPoseCheck
      ? await detectKeypoints(out, poseKey)
      : null;

    if (uploadContext) {
      setUploading(true);
      setUploadProgress(0);
      try {
        const url = await uploadToCloudinary(dataUrl, uploadContext, setUploadProgress);
        onCropComplete(url, finalDetection);
      } catch (err) {
        console.error('Cloudinary upload failed, falling back to base64:', err);
        onCropComplete(dataUrl, finalDetection);
      } finally {
        setUploading(false);
        setUploadProgress(0);
      }
    } else {
      onCropComplete(dataUrl, finalDetection);
    }
  };

  if (!open || !imageSrc) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        className="sm:max-w-md p-4 max-h-[100dvh] overflow-y-auto"
        data-testid="image-cropper-dialog"
      >
        <DialogHeader className="pb-1">
          <DialogTitle className="text-base">{title ?? t('imageCropper.adjustPhoto')}</DialogTitle>
        </DialogHeader>

        {/* Crop area */}
        <div
          ref={containerRef}
          className="relative overflow-hidden rounded-lg border bg-muted cursor-grab active:cursor-grabbing mx-auto"
          style={{ width: containerSize.w, height: containerSize.h, touchAction: 'none' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <canvas ref={canvasRef} className="w-full h-full" style={{ touchAction: 'none' }} />
          {canPoseCheck && (
            <PoseAlignFeedback
              detection={detection}
              poseKey={poseKey}
              className="absolute top-2 left-2 z-20"
            />
          )}
          {/* Ghost overlay — previous check-in photo */}
          {overlayMode === 'ghost' && previousPhoto && (
            <img
              src={previousPhoto}
              alt=""
              draggable={false}
              className="absolute inset-0 w-full h-full object-cover pointer-events-none"
              style={{ opacity: 0.25 }}
            />
          )}
          {/* Silhouette overlay — body outline for pose alignment */}
          {overlayMode === 'silhouette' && pose && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
              <BodySilhouetteOverlay
                pose={poseToVariant(pose)}
                className="h-[120%] -mt-[10%]"
                opacity={0.18}
              />
            </div>
          )}
          {pose && <PoseGuide pose={pose} />}
          {/* Overlay mode toggle button */}
          {pose && (
            <button
              type="button"
              className="absolute top-2 right-2 h-11 w-11 md:h-8 md:w-8 rounded-full bg-black/50 flex items-center justify-center pointer-events-auto z-10"
              onClick={(e) => { e.stopPropagation(); cycleOverlayMode(); }}
            >
              {overlayMode === 'ghost' && <Layers className="w-4 h-4 text-white" />}
              {overlayMode === 'silhouette' && <User className="w-4 h-4 text-white" />}
              {overlayMode === 'off' && <EyeOff className="w-4 h-4 text-white/60" />}
            </button>
          )}
          {circular ? (
            <>
              {/* Circular mask — darkens corners outside the circle */}
              <div className="absolute inset-0 pointer-events-none" style={{
                background: 'radial-gradient(circle closest-side at 50% 50%, transparent 98%, rgba(0,0,0,0.55) 100%)',
              }} />
              {/* Circular dashed border guide — forced square so it's never oval */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="border-2 border-dashed border-white/50 rounded-full pointer-events-none" style={{
                  width: Math.min(containerSize.w, containerSize.h) - 8,
                  height: Math.min(containerSize.w, containerSize.h) - 8,
                }} />
              </div>
            </>
          ) : (
            <div className="absolute inset-0 pointer-events-none border-2 border-dashed border-white/40 rounded-lg" />
          )}
        </div>

        {/* Zoom slider */}
        <div className="flex items-center gap-3">
          <ZoomOut className="w-4 h-4 text-muted-foreground flex-shrink-0" />
          <Slider
            value={[zoom]}
            onValueChange={([v]) => setZoom(v)}
            min={1} max={3} step={0.05}
            className="flex-1"
            data-testid="crop-zoom-slider"
          />
          <ZoomIn className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        </div>

        <p className="text-xs text-center text-muted-foreground -mt-1">{t('imageCropper.hint')}</p>

        {/* Upload progress bar */}
        {uploading && (
          <div className="w-full mt-2">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>{t('imageCropper.uploading')}</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        )}

        {/* Action buttons — always visible */}
        <div className="flex gap-2 pt-1">
          <Button className="flex-1 bg-primary hover:bg-primary/90" onClick={handleCrop} disabled={uploading} data-testid="crop-confirm-btn">
            <Check className="w-4 h-4 mr-1" /> {uploading ? t('imageCropper.uploading') : t('imageCropper.apply')}
          </Button>
          <Button variant="outline" className="flex-1" onClick={onClose} data-testid="crop-cancel-btn">
            <X className="w-4 h-4 mr-1" /> {t('common.cancel')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
